import { Game, Play, db } from "../../ClientDatabase/DataBaseService";

// Game operations
export const GameService = {
  async createGame(game: Omit<Game, 'id' | 'synced'>): Promise<number> {
    return await db.games.add({
      ...game,
      synced: false
    });
  },
  
  async getGames(): Promise<Game[]> {
    return await db.games.toArray();
  },
  
  async getGame(id: number): Promise<Game | undefined> {
    return await db.games.get(id);
  },
  
  async updateGame(id: number, changes: Partial<Game>): Promise<number> {
    // Mark as unsynced when updated
    return await db.games.update(id, { ...changes, synced: false });
  },
  
  async deleteGame(id: number): Promise<void> {
    // Also delete all plays associated with this game
    await db.transaction('rw', [db.games, db.plays], async () => {
      await db.plays.where('gameId').equals(id).delete();
      await db.games.delete(id);
    });
  },

  async getUnsyncedGames(): Promise<Game[]> {
    return await db.games.filter(game => game.synced === false).toArray();
  }
};

// Play operations
export const PlayService = {
  async addPlay(play: Omit<Play, 'id' | 'synced'>): Promise<number> {
    return await db.plays.add({
      ...play,
      timestamp: play.timestamp || Date.now(),
      synced: false
    });
  },
  
  async getPlaysForGame(gameId: number): Promise<Play[]> {
    return await db.plays
      .where('gameId')
      .equals(gameId)
      .sortBy('timestamp');
  },
  
  async updatePlay(id: number, changes: Partial<Play>): Promise<number> {
    return await db.plays.update(id, { ...changes, synced: false });
  },
  
  async deletePlay(id: number): Promise<void> {
    await db.plays.delete(id);
  },

  async getUnsyncedPlays(): Promise<Play[]> {
    return await db.plays.filter(play => play.synced === false).toArray();
  }
};

// Sync operations
export const SyncService = {
  async getUnsyncedGames(): Promise<Game[]> {
    return await db.games.filter(game => game.synced === false).toArray();
  },
  
  async getUnsyncedPlays(): Promise<Play[]> {
    return await db.plays.filter(play => play.synced === false).toArray();
  },
  
  async markGameSynced(id: number): Promise<number> {
    return await db.games.update(id, { synced: true });
  },
  
  async markPlaySynced(id: number): Promise<number> {
    return await db.plays.update(id, { synced: true });
  }
};

// Network status monitoring
export const NetworkService = {
  isOnline(): boolean {
    return navigator.onLine;
  },
  
  addOnlineListener(callback: () => void): void {
    window.addEventListener('online', callback);
  },
  
  addOfflineListener(callback: () => void): void {
    window.addEventListener('offline', callback);
  },
  
  removeOnlineListener(callback: () => void): void {
    window.removeEventListener('online', callback);
  },
  
  removeOfflineListener(callback: () => void): void {
    window.removeEventListener('offline', callback);
  }
};